import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Save, AlertCircle } from "lucide-react";
import { createClient } from "../../../api/chatbotApi";

const initialForm = { name: "", address: "{}", contact_details: "{}" };

export default function ChatbotClientCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    setSaving(true);
    setError("");
    try {
      let address = {};
      let contact_details = {};
      try {
        address = JSON.parse(form.address);
      } catch {
        setError("Invalid JSON in address field");
        setSaving(false);
        return;
      }
      try {
        contact_details = JSON.parse(form.contact_details);
      } catch {
        setError("Invalid JSON in contact details field");
        setSaving(false);
        return;
      }

      await createClient({ name: form.name.trim(), address, contact_details });
      navigate("/clients");
    } catch (err) {
      setError(err.response?.data?.detail || "Error creating client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/clients" className="inline-flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft size={16} /> Clients
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">New Client</span>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 border-b border-slate-100 pb-5">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Building2 size={24} className="text-[#096dea]" /> Create New Client
          </h1>
          <p className="mt-1 text-sm text-slate-500">Register a top-level chatbot organization and client account.</p>
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-5">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
              placeholder="Acme Corp"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Address (JSON)</label>
            <textarea
              value={form.address}
              onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 font-mono text-xs focus:border-[#096dea] focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">Contact Details (JSON)</label>
            <textarea
              value={form.contact_details}
              onChange={(e) => setForm((current) => ({ ...current, contact_details: e.target.value }))}
              rows={4}
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 font-mono text-xs focus:border-[#096dea] focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-5">
          <Link to="/clients" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
            Cancel
          </Link>
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving || !form.name.trim()}
            className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:opacity-50"
          >
            <Save size={16} /> {saving ? "Creating..." : "Create Client"}
          </button>
        </div>
      </div>
    </div>
  );
}
